/*
 * fc-first-tap-fix.js — Fit Creamery custom fix (safe to keep across theme updates)
 * ---------------------------------------------------------------------------------
 * Problem: The Eurus theme defers most of its JavaScript until the shopper's FIRST
 * interaction (see the `runDispatch` / `init-run` handler on <body> in theme.liquid).
 * On touch devices that first tap boots everything and gets swallowed, so the first
 * tap on a link/button does nothing and the shopper has to tap again. The theme ships
 * a workaround for this — but only for the search, cart, and mobile-nav icons:
 *     setTimeout(() => el.click(), 200)
 *
 * This script generalizes that same, proven workaround to every real navigation link,
 * so the hero CTA and every other on-page button register on the first tap.
 *
 * Why it's safe / self-correcting:
 *   - Runs at most ONCE, on the very first touch of the page session.
 *   - Only re-fires clicks on <a href> that actually navigate (skips "#" and JS anchors).
 *   - Waits for the finger to lift and bails if it moved, so a scroll that happens to
 *     start on a link is never mistaken for a tap on it.
 *   - If the first tap DID work, the page navigates away and the re-fire timer never
 *     runs. If the first tap was swallowed, the re-fire completes the navigation.
 *
 * Loaded via a Custom Liquid block in the footer group (theme setting data), so no
 * Eurus core file is modified and a theme update won't overwrite it.
 */
(function () {
  'use strict';

  // Only touch devices exhibit the swallowed-first-tap behavior.
  if (!('ontouchstart' in window)) return;

  // How far a finger may drift and still count as a tap rather than a scroll.
  var MOVE_TOLERANCE = 10;

  var handled = false;

  function onFirstTouch(event) {
    if (handled) return;
    handled = true;
    document.removeEventListener('touchstart', onFirstTouch, true);

    var target = event.target;
    if (!target || !target.closest) return;

    var link = target.closest('a[href]');
    if (!link) return;

    // Let the theme keep handling its own special cases (search / cart / mobile nav).
    if (link.closest('#SearchOpen, #cart-icon, #mobile-navigation')) return;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;      // in-page anchors / JS-only links
    if (link.hasAttribute('data-no-first-tap-fix')) return; // manual opt-out escape hatch
    if (link.hasAttribute('download')) return;              // browser handles it once; don't double it
    var linkTarget = link.getAttribute('target');
    if (linkTarget && linkTarget !== '_self') return;       // new tab/window: re-firing would open a second

    // A touch is not yet a tap. If the finger moves, this was a scroll that merely
    // STARTED on the link, and re-firing would navigate out from under the shopper —
    // very easy to trigger on link-dense grids like the recipe index. So watch the
    // finger and only re-fire once it lifts without having travelled.
    var touch = event.touches && event.touches[0];
    if (!touch || event.touches.length > 1) return;   // no coords, or a pinch/multi-touch
    var startX = touch.clientX;
    var startY = touch.clientY;
    var moved = false;

    function onMove(e) {
      if (moved) return;
      var t = e.touches && e.touches[0];
      if (!t) return;
      if (e.touches.length > 1 ||
          Math.abs(t.clientX - startX) > MOVE_TOLERANCE ||
          Math.abs(t.clientY - startY) > MOVE_TOLERANCE) {
        moved = true;
      }
    }

    function stopWatching() {
      document.removeEventListener('touchmove', onMove, true);
      document.removeEventListener('touchend', onEnd, true);
      document.removeEventListener('touchcancel', stopWatching, true);
    }

    function onEnd() {
      stopWatching();
      if (moved) return;                              // it was a scroll — leave the page alone

      // Same 200ms delay the theme itself uses for its search/cart/nav workaround,
      // giving the deferred init a moment to settle before we re-fire the tap.
      setTimeout(function () { link.click(); }, 200);
    }

    document.addEventListener('touchmove', onMove, true);
    document.addEventListener('touchend', onEnd, true);
    document.addEventListener('touchcancel', stopWatching, true);
  }

  // Capture phase so we see the first touch regardless of other listeners.
  document.addEventListener('touchstart', onFirstTouch, true);

  // ── On-page CTA first-tap navigation ───────────────────────────────────────
  // The re-fire above only rescues the VERY FIRST tap on the page. Custom on-page
  // CTAs that a shopper reaches only after scrolling (so they're never the first
  // touch) can still lose their first tap to the theme's deferred init. These are
  // plain <a href> links, so navigating on pointerup (the real finger release)
  // makes them reliable on the first tap regardless of when the click is dropped.
  // Scoped to our custom CTA classes only — stateful widget <button>s are
  // deliberately excluded so nothing double-fires. (The floating CTA bar does the
  // same thing inside its own section script.)
  var CTA_SELECTOR = '.cta-headline__btn, .custom-testimonials__btn, .htv__cta-btn, .frb__link';
  var downEl = null, downX = 0, downY = 0, ctaMoved = false;
  document.addEventListener('pointerdown', function (e) {
    downEl = (e.target.closest && e.target.closest(CTA_SELECTOR)) || null;
    downX = e.clientX; downY = e.clientY; ctaMoved = false;
  }, true);
  document.addEventListener('pointermove', function (e) {
    if (Math.abs(e.clientX - downX) > MOVE_TOLERANCE || Math.abs(e.clientY - downY) > MOVE_TOLERANCE) ctaMoved = true;
  }, true);
  document.addEventListener('pointerup', function (e) {
    var el = downEl; downEl = null;
    if (!el || ctaMoved) return;
    if (e.pointerType === 'mouse') return;                       // desktop: native click navigates
    if (!e.target.closest || e.target.closest(CTA_SELECTOR) !== el) return;
    if (el.getAttribute('aria-disabled') === 'true') return;
    if (el.hasAttribute('download')) return;
    var tgt = el.getAttribute('target');
    if (tgt && tgt !== '_self') return;                          // opens a new context: let native handle
    var href = el.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;                 // no real destination
    window.location.href = href;
  }, true);
})();
